// THE SIDE MENU, as data (sidebar-navigation, owner 2026-10-31).
//
// Grouped by the workflow: sell it, buy it from the OEM, what we carry, what it
// costs, run the system. Pure and React-free apart from the icon references, so
// the role rules and the "every route has a menu item" proof are tested here.
//
// ⚠️ ONLY THE MENU LIVES HERE. Every `to` is a route in App.js and keeps its
// path. An item is a real page: a modal (New Quote), a per-quote panel (customer
// PO, price requests) or a tab inside a page (Warehouses, Users) is not an item.
//
// ⚠️ HIDING AN ITEM IS A COURTESY, NOT ACCESS CONTROL: the backend refuses the
// same reads on its own.

import type { ComponentType } from "react";
import {
  FileText, Boxes, Truck, Tag, Warehouse, Settings, BarChart3, ClipboardCheck,
  FileSpreadsheet, PackageCheck, Briefcase, Factory, Package, Percent, ShieldCheck,
} from "lucide-react";

export type NavIcon = ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

/** What a user must be able to do to see an item; absent = every role. */
export type NavRequires = "reports" | "admin";

export type NavItem = {
  to: string;
  /** Stable test id, independent of the label. */
  id: string;
  label: string;
  icon: NavIcon;
  requires?: NavRequires;
  /** Other paths that are this page, for the highlight and the open group. */
  alsoActive?: string[];
};

export type NavGroup = {
  id: string;
  label: string;
  icon: NavIcon;
  /** Open until the user says otherwise. The daily work is; reference data and
   *  administration are not. */
  defaultOpen: boolean;
  items: NavItem[];
};

export type NavCapabilities = { reports: boolean; admin: boolean };

// ⚠️ THERE IS ONE QUOTE LIST. "Saved" pointed at /saved-quotes, a second list of
// the same records; that route redirects to /quotes, so an item for it would be
// the same screen twice. /saved-quotes/:id is a quote, hence alsoActive.
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "sales", label: "Sales", icon: Briefcase, defaultOpen: true,
    items: [
      { to: "/quotes", id: "quotes", label: "Quotes", icon: FileText, alsoActive: ["/saved-quotes"] },
      { to: "/reports", id: "reports", label: "Reports", icon: BarChart3, requires: "reports" },
    ],
  },
  {
    id: "oem", label: "OEM", icon: Factory, defaultOpen: true,
    items: [
      { to: "/oem-quotes", id: "oem-quotes", label: "OEM Quotes", icon: FileSpreadsheet },
      { to: "/oem-orders", id: "oem-orders", label: "OEM Orders", icon: PackageCheck },
      // M2 (D197): the OEM master.
      { to: "/oems", id: "oems", label: "OEMs", icon: Factory },
    ],
  },
  {
    id: "products", label: "Products", icon: Package, defaultOpen: false,
    items: [
      { to: "/catalog", id: "catalog", label: "Catalog", icon: Boxes },
      { to: "/inventory", id: "inventory", label: "Inventory", icon: Warehouse },
    ],
  },
  {
    id: "pricing", label: "Pricing", icon: Percent, defaultOpen: false,
    items: [
      { to: "/pricing", id: "pricing", label: "Pricing Rules", icon: Tag },
      { to: "/sourcing", id: "sourcing", label: "Sourcing", icon: Truck },
    ],
  },
  {
    id: "admin", label: "Admin", icon: ShieldCheck, defaultOpen: false,
    items: [
      { to: "/readiness", id: "readiness", label: "Readiness", icon: ClipboardCheck, requires: "admin" },
      { to: "/settings", id: "settings", label: "Settings", icon: Settings },
    ],
  },
];

/** The menu one user sees: items they cannot use removed, and a group left with
 *  nothing removed with them. A group left with ONE item is still returned with
 *  that one item; `isDropdown` says it renders as a plain link. */
export function visibleNav(can: NavCapabilities, groups: NavGroup[] = NAV_GROUPS): NavGroup[] {
  const allowed = (item: NavItem) => !item.requires || can[item.requires];
  return groups
    .map((g) => ({ ...g, items: g.items.filter(allowed) }))
    .filter((g) => g.items.length > 0);
}

export const isDropdown = (group: NavGroup): boolean => group.items.length > 1;

export function itemMatchesPath(item: NavItem, pathname: string): boolean {
  return [item.to, ...(item.alsoActive ?? [])]
    .some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** The group holding the current page, or null (no group holds it). */
export function activeGroupId(groups: NavGroup[], pathname: string): string | null {
  return groups.find((g) => g.items.some((i) => itemMatchesPath(i, pathname)))?.id ?? null;
}
